/**
 * The route tree (routing-and-state.md §1 route map + §9 guards). Built with TanStack
 * Router's code-based API — the same route tree the file-based convention in §2 would
 * generate, chosen here because the guard behaviour (esp. the ENG-11 slow-refresh race)
 * is the load-bearing property and is directly unit-testable this way.
 *
 * Guards (§9):
 *  - `_authenticated`  → ENG-11: AWAIT an in-flight refresh before deciding, so a slow
 *                        refresh never bounces an authenticated user; an unauthenticated
 *                        user is redirected only after refresh resolves negative. It also
 *                        requires a BOUND FIRM (a firm principal): a signed-in identity with
 *                        no firm session is sent to the firm picker (/firms).
 *  - `_settings`       → managing_partner | firm_admin.
 *  - `_partner-only`   → managing_partner ONLY (the wall: firm_admin cannot reach it).
 */
import {
  createRootRouteWithContext,
  createRoute,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { JSX } from 'react';

import { roleCan } from '@argus/permissions-catalogue';

import { AppShell } from '../components/app-shell.tsx';
import { OfflineBanner, UpdateBanner } from '../components/pwa/pwa-controls.tsx';
import { AuditExportScreen } from '../screens/settings/audit-export-screen.tsx';
import { AuditLogScreen } from '../screens/settings/audit-log-screen.tsx';
import { DsarScreen } from '../screens/settings/dsar-screen.tsx';
import { DashboardScreen } from '../screens/dashboard-screen.tsx';
import { FirmPickerScreen } from '../screens/firm-picker-screen.tsx';
import { FirmSettingsScreen } from '../screens/settings/firm-settings-screen.tsx';
import { ForgotPasswordScreen } from '../screens/forgot-password-screen.tsx';
import { InviteScreen } from '../screens/invite-screen.tsx';
import { LoginScreen } from '../screens/login-screen.tsx';
import { NotFoundScreen } from '../screens/not-found-screen.tsx';
import { RecoveryScreen } from '../screens/recovery-screen.tsx';
import { ResetPasswordScreen } from '../screens/reset-password-screen.tsx';
import { SecuritySettingsScreen } from '../screens/settings/security-settings-screen.tsx';
import { TeamScreen } from '../screens/settings/team-screen.tsx';
import { TotpEnrolScreen } from '../screens/totp-enrol-screen.tsx';
import { TotpScreen } from '../screens/totp-screen.tsx';
import type { RouterContext } from './context.ts';

/**
 * The root layout. The PWA update prompt (§10) is mounted HERE — above every route, public ones
 * included (login, invite, firm picker) — not inside the firm-bound AppShell: `useRegisterSW` in
 * <UpdateBanner/> is also what registers the service worker, and a signed-out user must be able
 * to see "Update available" (the August build mounted it only in the AppShell, so an invitee on a
 * stale bundle had nothing on screen to reload with).
 */
function RootComponent(): JSX.Element {
  return (
    <>
      <UpdateBanner />
      <OfflineBanner />
      <Outlet />
    </>
  );
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  // PO finding 2026-10-05 #5: a path no route matches (e.g. `/no-such-page`) bubbles the
  // notFound up to the nearest ancestor that declares one — the root is the deepest match
  // for a path with no matching descendant, so this is effectively the app's default 404.
  notFoundComponent: NotFoundScreen,
});

/** `/` → resolve auth, then /login (no identity), /firms (no firm bound) or /dashboard. */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async ({ context }) => {
    await context.auth.ensureResolved();
    if (context.auth.status !== 'authenticated') throw redirect({ to: '/login' });
    throw redirect({ to: context.auth.user ? '/dashboard' : '/firms' });
  },
});

/**
 * The sign-in steps (and the firm picker) carry an optional `redirect` — honoured only for an
 * invite or an allowlisted return path (see redirect.ts).
 */
function redirectSearch(search: Record<string, unknown>): { redirect?: string } {
  const redirectTo = search['redirect'];
  return typeof redirectTo === 'string' ? { redirect: redirectTo } : {};
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: redirectSearch,
  component: LoginScreen,
});

const loginTotpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/totp',
  validateSearch: redirectSearch,
  component: TotpScreen,
});

const loginRecoveryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/recovery',
  validateSearch: redirectSearch,
  component: RecoveryScreen,
});

/**
 * `/login/totp-enrol` (ENG-09 / T-1.17). After invite-accept / an enrolment-required sign-in,
 * before the firm picker. Guarded so an UNauthenticated user → /login and an identity that is
 * ALREADY enrolled → /firms (TOTP is a global identity factor, read off the identity).
 */
const totpEnrolRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/totp-enrol',
  validateSearch: redirectSearch,
  beforeLoad: async ({ context }) => {
    await context.auth.ensureResolved();
    if (context.auth.status !== 'authenticated') {
      throw redirect({ to: '/login' });
    }
    if (context.auth.identity?.totp_enrolled === true) {
      throw redirect({ to: '/firms' });
    }
  },
  component: TotpEnrolScreen,
});

/**
 * `/firms` (ADR-0022 B10) — the sign-in-once firm-picker. Top-level (a full-screen auth
 * card, NOT inside the firm-bound AppShell — no firm is selected yet), guarded like
 * `/login/totp-enrol`: an UNauthenticated identity → /login. It requires only an IDENTITY
 * session (status `authenticated`); the firm session is minted on select inside the
 * screen. The legacy subdomain flow never routes here (firm implied by the subdomain).
 * An optional `redirect` is where the picker continues after selecting — honoured only for an
 * allowlisted return path (router/redirect.ts), else /dashboard.
 */
const firmsPickerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/firms',
  validateSearch: redirectSearch,
  beforeLoad: async ({ context }) => {
    await context.auth.ensureResolved();
    if (context.auth.status !== 'authenticated') {
      throw redirect({ to: '/login' });
    }
  },
  component: FirmPickerScreen,
});

/**
 * `/forgot-password` carries the same optional `redirect` as the sign-in steps so the
 * login → forgot → login round trip keeps an invitation / allowlisted return path (F-33).
 */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  validateSearch: redirectSearch,
  component: ForgotPasswordScreen,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password/$token',
  component: function ResetPasswordRouteComponent(): JSX.Element {
    const { token } = resetPasswordRoute.useParams();
    return <ResetPasswordScreen token={token} />;
  },
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: function InviteRouteComponent(): JSX.Element {
    const { token } = inviteRoute.useParams();
    return <InviteScreen token={token} />;
  },
});

/** `_authenticated` — ENG-11 guard: await the in-flight refresh before deciding. */
export const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authenticated',
  beforeLoad: async ({ context, location }) => {
    // ENG-11: when status is `loading`, this AWAITS the in-flight refresh() (main.tsx
    // kicked it off on start); it returns immediately once already resolved.
    await context.auth.ensureResolved();
    if (context.auth.status !== 'authenticated') {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    // Signed in, but no firm session bound (fresh sign-in, reload after the firm session
    // lapsed, or a refused select): every screen below is firm data — choose a firm first.
    if (context.auth.user === null) {
      throw redirect({ to: '/firms' });
    }
  },
  component: AppShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/dashboard',
  component: DashboardScreen,
});

// ── Regulatory reference (T-6.05/T-6.06 / US-120/121/122). A top-level browse
// surface (CBN guide history + statute library + the Tax Law Reference dialog).
// No role gate — the backend `/reference/*` routes gate on requireAuth only, so
// every authenticated firm user reads the regulatory library (it is public
// material). Lazy-loaded through a single dynamic import so all reference UI ships
// in its OWN `reference-*.js` chunk (the T-6.05 ≤ 50 KB gzip route budget), out of
// the main entry.
const loadReferenceScreen = (): Promise<
  typeof import('../screens/reference/reference-screen.tsx')
> => import('../screens/reference/reference-screen.tsx');

/** F-59: the two reference tables are tabs, deep-linkable as `?tab=`. */
export type ReferenceTab = 'guides' | 'statutes';
export interface ReferenceSearch {
  tab?: ReferenceTab;
}

export const referenceRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/reference',
  validateSearch: (search: Record<string, unknown>): ReferenceSearch =>
    search['tab'] === 'statutes' ? { tab: 'statutes' } : {},
  component: lazyRouteComponent(loadReferenceScreen, 'ReferenceScreen'),
});

// ── Cases (T-2.11 / US-010…013). Routes address cases by the human `case_number`
// (ARG-YYYY-NNNN) — the `api.cases.*` client speaks case_number, so params pass straight
// through. Every case screen is reached ONLY via `lazyRouteComponent` over one dynamic
// import, so all case UI ships in a single lazily-loaded `cases-*.js` chunk (the T-2.11
// per-route budget), never in the main entry. Guards mirror the §9 idiom but gate on the
// ENG-10 catalogue (CC-2): firm_admin holds no `cases.*` and is bounced to /dashboard.
const loadCaseScreens = (): Promise<typeof import('../screens/cases/cases-routes.tsx')> =>
  import('../screens/cases/cases-routes.tsx');

const STATUS_FILTERS = ['active', 'all', 'open', 'in_review', 'closed', 'archived'] as const;
type CaseStatusFilterParam = (typeof STATUS_FILTERS)[number];
// F-03: the closed `GET /cases?sort=&dir=` sets (mirrors `CaseSortKey` / `CaseSortDir` in
// lib/api.ts). Both are optional — absent means the server default (created_at desc).
const CASE_SORT_KEYS = ['created_at', 'updated_at', 'risk_score'] as const;
const CASE_SORT_DIRS = ['asc', 'desc'] as const;
type CaseSortKeyParam = (typeof CASE_SORT_KEYS)[number];
type CaseSortDirParam = (typeof CASE_SORT_DIRS)[number];
// Fields are declared optional so links/navigations to `/cases` need not spell out the
// whole filter set (`<Link to="/cases" />` just works); `validateSearch` still normalises
// every param to a concrete default at runtime, and the list screen reads with fallbacks.
interface CasesSearch {
  status?: CaseStatusFilterParam;
  mine?: boolean;
  q?: string;
  page?: number;
  // `| undefined` on purpose: the router merges the PARENT routes' raw (unvalidated) search
  // under this route's validated one, so an invalid `?sort=bogus` would leak through unless
  // validateSearch always emits the key — `undefined` here means "absent".
  sort?: CaseSortKeyParam | undefined;
  dir?: CaseSortDirParam | undefined;
}

/** `/cases` — list (US-011). Needs `cases.list`. Typed, bookmarkable search params (§4). */
export const casesListRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/cases',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role === undefined || !roleCan(role, 'cases.list')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  validateSearch: (search: Record<string, unknown>): CasesSearch => {
    const status = STATUS_FILTERS.includes(search['status'] as CaseStatusFilterParam)
      ? (search['status'] as CaseStatusFilterParam)
      : 'active';
    const mine = search['mine'] === true || search['mine'] === 'true';
    const q = typeof search['q'] === 'string' ? search['q'] : '';
    const rawPage =
      typeof search['page'] === 'number'
        ? search['page']
        : Number.parseInt(String(search['page'] ?? ''), 10);
    const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;
    // F-03: an invalid sort/dir becomes `undefined` (the key is ALWAYS emitted — see
    // CasesSearch); the list screen then omits it from the request and shows the default.
    const sort = CASE_SORT_KEYS.includes(search['sort'] as CaseSortKeyParam)
      ? (search['sort'] as CaseSortKeyParam)
      : undefined;
    const dir = CASE_SORT_DIRS.includes(search['dir'] as CaseSortDirParam)
      ? (search['dir'] as CaseSortDirParam)
      : undefined;
    return { status, mine, q, page, sort, dir };
  },
  component: lazyRouteComponent(loadCaseScreens, 'CasesListScreen'),
});

/**
 * `/cases/new` — create (US-010). Needs `cases.create` (managing_partner | senior_associate).
 * An optional `client_id` search param (the client screen's "New case for this client" link)
 * preselects that client in the `ClientPicker`.
 */
export const caseCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/cases/new',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role === undefined || !roleCan(role, 'cases.create')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  validateSearch: (search: Record<string, unknown>): { client_id?: string } => {
    const clientId = search['client_id'];
    return typeof clientId === 'string' && clientId !== '' ? { client_id: clientId } : {};
  },
  component: lazyRouteComponent(loadCaseScreens, 'CaseCreateScreen'),
});

/**
 * `/cases/share-target` — the PWA `share_target` landing + "Pick a case" screen
 * (offline-and-pwa.md §2; T-3.13 / US-026). Authenticated-only (no `cases.*`
 * gate — the shared files are the caller's own firm inbox; the case selector
 * re-gates on cases.create/read as it navigates). Static path, so it out-ranks
 * `/cases/$caseNumber` at match time. `?ids=` carries the comma-separated inbox
 * receipt ULIDs the ingest POST redirected here with.
 */
export const caseShareTargetRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/cases/share-target',
  validateSearch: (search: Record<string, unknown>): { ids?: string } => {
    const ids = search['ids'];
    return typeof ids === 'string' && ids !== '' ? { ids } : {};
  },
  component: lazyRouteComponent(loadCaseScreens, 'ShareTargetScreen'),
});

/** `/cases/$caseNumber` — overview/dashboard (US-012/013). Needs `cases.read`. */
export const caseOverviewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/cases/$caseNumber',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role === undefined || !roleCan(role, 'cases.read')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: lazyRouteComponent(loadCaseScreens, 'CaseOverviewScreen'),
});

/**
 * `/cases/$caseNumber/statement-analysis` — the analyst statement-analysis workspace
 * (US-030/031/032; T-4.10). Needs `documents.read` (the transactions read perm); ships in
 * the same lazily-loaded `cases-*.js` chunk as the other case screens. Static tail segment
 * out-ranks the bare `/cases/$caseNumber` overview at match time.
 */
export const statementAnalysisRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/cases/$caseNumber/statement-analysis',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role === undefined || !roleCan(role, 'documents.read')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: lazyRouteComponent(loadCaseScreens, 'StatementAnalysisScreen'),
});

/**
 * `/cases/$caseNumber/counterparties` — the analyst counterparty + sanctions workspace
 * (US-130; T-4.15). Needs `documents.read` (the counterparty-directory read perm); ships in
 * the same lazily-loaded `cases-*.js` chunk as the other case screens. Static tail segment
 * out-ranks the bare `/cases/$caseNumber` overview at match time.
 */
export const counterpartiesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/cases/$caseNumber/counterparties',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role === undefined || !roleCan(role, 'documents.read')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: lazyRouteComponent(loadCaseScreens, 'CounterpartiesScreen'),
});

/**
 * `/cases/$caseNumber/audit` — the case audit-trail page (US-080; T-6.14).
 * Needs `audit.read` (every firm role EXCEPT client); ships in the same
 * lazily-loaded `cases-*.js` chunk. Static tail segment out-ranks the bare
 * `/cases/$caseNumber` overview at match time. The filters (`from` / `to` ISO dates,
 * `action` event token, `user` actor id) and `page` are typed search params so a filtered
 * view is bookmarkable (UI/UX follow-up F-16); every key is optional and `validateSearch`
 * drops empty/invalid ones, so the screen reads with fallbacks and never writes `undefined`
 * (`exactOptionalPropertyTypes`).
 */
export interface CaseAuditSearch {
  from?: string;
  to?: string;
  action?: string;
  user?: string;
  page?: number;
}
const AUDIT_TEXT_FILTERS = ['from', 'to', 'action', 'user'] as const;

export const caseAuditRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/cases/$caseNumber/audit',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role === undefined || !roleCan(role, 'audit.read')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  validateSearch: (search: Record<string, unknown>): CaseAuditSearch => {
    const out: CaseAuditSearch = {};
    for (const key of AUDIT_TEXT_FILTERS) {
      const value = search[key];
      if (typeof value === 'string' && value !== '') out[key] = value;
    }
    const rawPage =
      typeof search['page'] === 'number'
        ? search['page']
        : Number.parseInt(String(search['page'] ?? ''), 10);
    if (Number.isInteger(rawPage) && rawPage > 1) out.page = rawPage;
    return out;
  },
  component: lazyRouteComponent(loadCaseScreens, 'AuditScreen'),
});

/**
 * `/cases/$caseNumber/reports` — the Reports workspace (US-070; T-6.10). Needs
 * `reports.list` (every firm role EXCEPT firm_admin holds it — managing_partner,
 * senior_associate, associate, paralegal, client); ships in the same lazily-loaded
 * `cases-*.js` chunk. Static tail segment out-ranks the bare `/cases/$caseNumber`
 * overview at match time.
 */
export const reportsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/cases/$caseNumber/reports',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role === undefined || !roleCan(role, 'reports.list')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: lazyRouteComponent(loadCaseScreens, 'ReportsScreen'),
});

/**
 * `/cases/$caseNumber/documents/$documentId` — the document page (F-56: documents open on a
 * page, not a modal). Needs `documents.read`; its own lazily-loaded chunk. Static segments
 * out-rank the bare `/cases/$caseNumber` overview at match time.
 */
export const documentRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/cases/$caseNumber/documents/$documentId',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role === undefined || !roleCan(role, 'documents.read')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: lazyRouteComponent(
    () => import('../screens/documents/document-screen.tsx'),
    'DocumentScreen',
  ),
});

// ── Clients (PO finding 2026-10-05 — "a client entity"). Addressed by the ULID `client_id`.
// Lazily loaded through a single dynamic import (its own `clients-routes-*.js` chunk), out of
// both the main entry and the `cases-routes-*.js` chunk; gated like `/cases` (`cases.list`).
const loadClientScreens = (): Promise<typeof import('../screens/clients/clients-routes.tsx')> =>
  import('../screens/clients/clients-routes.tsx');

interface ClientsSearch {
  q?: string;
  page?: number;
}

/** `/clients` — the client directory list. Needs `cases.list` (same gate as `/cases`). */
export const clientsListRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/clients',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role === undefined || !roleCan(role, 'cases.list')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  validateSearch: (search: Record<string, unknown>): ClientsSearch => {
    const q = typeof search['q'] === 'string' ? search['q'] : '';
    const rawPage =
      typeof search['page'] === 'number'
        ? search['page']
        : Number.parseInt(String(search['page'] ?? ''), 10);
    const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;
    return { q, page };
  },
  component: lazyRouteComponent(loadClientScreens, 'ClientsListScreen'),
});

/** `/clients/$clientId` — one client + the case rows the caller may see. Needs `cases.list`. */
export const clientRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/clients/$clientId',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role === undefined || !roleCan(role, 'cases.list')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: lazyRouteComponent(loadClientScreens, 'ClientScreen'),
});

/** `_settings` — managing_partner | firm_admin (firm-level settings). */
export const settingsLayoutRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: '_settings',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role !== 'managing_partner' && role !== 'firm_admin') {
      throw redirect({ to: '/dashboard' });
    }
  },
});

const firmSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/firm',
  component: FirmSettingsScreen,
});

/** `/settings/team` — the T-1.16-era users/admin list (owed #15 "Reset TOTP" host).
 *  Rides the `_settings` layout (managing_partner | firm_admin — the users.read
 *  holders); the reset affordance itself is MP-only inside the screen. */
const teamRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/team',
  component: TeamScreen,
});

/**
 * `/settings/security` — SELF-SERVICE security settings (owed #15 re-enrol
 * authenticator). Deliberately under `_authenticated`, NOT `_settings`: it
 * operates only on the caller's own credentials, so every role reaches it —
 * including a recovery-code session (the lost-device path).
 */
const securitySettingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/security',
  component: SecuritySettingsScreen,
});

/** `_partner-only` — managing_partner ONLY (the wall: firm_admin is excluded). */
export const partnerOnlyLayoutRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: '_partner-only',
  beforeLoad: ({ context }) => {
    if (context.auth.user?.role !== 'managing_partner') {
      throw redirect({ to: '/dashboard' });
    }
  },
});

const auditExportRoute = createRoute({
  getParentRoute: () => partnerOnlyLayoutRoute,
  path: '/settings/audit-export',
  component: AuditExportScreen,
});

/** `/settings/dsar` — the DSAR fulfilment queue (T-7.14 / US-153). Rides the
 *  `_partner-only` layout (managing_partner ONLY — firm_admin is walled out, like
 *  audit-export); the queue read is `dsar.read`, the fulfilment affordances are
 *  `dsar.manage`-gated inside the screen. */
const dsarRoute = createRoute({
  getParentRoute: () => partnerOnlyLayoutRoute,
  path: '/settings/dsar',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role === undefined || !roleCan(role, 'dsar.read')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: DsarScreen,
});

/**
 * `/settings/audit` — the firm-wide audit log (F-58). Needs `audit.read` (every firm
 * role except client); rides the `_authenticated` layout directly because the `_settings`
 * layout is managing_partner | firm_admin only, narrower than the permission. Filters and
 * page are typed search params so a filtered view is bookmarkable.
 */
export interface AuditLogSearch {
  from?: string;
  to?: string;
  action?: string;
  user?: string;
  resource_type?: string;
  resource_id?: string;
  case_id?: string;
  page?: number;
}
const AUDIT_LOG_TEXT_FILTERS = [
  'from',
  'to',
  'action',
  'user',
  'resource_type',
  'resource_id',
  'case_id',
] as const;

export const auditLogRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/audit',
  beforeLoad: ({ context }) => {
    const role = context.auth.user?.role;
    if (role === undefined || !roleCan(role, 'audit.read')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  validateSearch: (search: Record<string, unknown>): AuditLogSearch => {
    const out: AuditLogSearch = {};
    for (const key of AUDIT_LOG_TEXT_FILTERS) {
      const value = search[key];
      if (typeof value === 'string' && value !== '') out[key] = value;
    }
    const rawPage =
      typeof search['page'] === 'number'
        ? search['page']
        : Number.parseInt(String(search['page'] ?? ''), 10);
    if (Number.isInteger(rawPage) && rawPage > 1) out.page = rawPage;
    return out;
  },
  component: AuditLogScreen,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  loginTotpRoute,
  loginRecoveryRoute,
  totpEnrolRoute,
  firmsPickerRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  inviteRoute,
  authenticatedRoute.addChildren([
    dashboardRoute,
    referenceRoute,
    casesListRoute,
    caseCreateRoute,
    caseShareTargetRoute,
    caseOverviewRoute,
    clientsListRoute,
    clientRoute,
    statementAnalysisRoute,
    counterpartiesRoute,
    caseAuditRoute,
    reportsRoute,
    documentRoute,
    securitySettingsRoute,
    auditLogRoute,
    settingsLayoutRoute.addChildren([firmSettingsRoute, teamRoute]),
    partnerOnlyLayoutRoute.addChildren([auditExportRoute, dsarRoute]),
  ]),
]);
